import { useEffect, useState } from 'react'
import {Building2, Search, Globe, Phone, ChevronLeft, ChevronRight, X, Mail, MapPin, Users, Briefcase, FileText, ExternalLink, Linkedin, Instagram, Facebook, Youtube, Twitter, Trash2} from 'lucide-react'
import AdminLayout from '@/components/AdminLayout'
import { lumi } from '@/lib/lumi'

const PAGE_SIZE = 15

interface Company {
  _id: string
  userId?: string
  tradeName?: string
  displayName?: string
  commercialName?: string
  legalName?: string
  rfc?: string
  industry?: string
  customIndustry?: string
  address?: string
  city?: string
  state?: string
  country?: string
  phone?: string
  whatsapp?: string
  website?: string
  email?: string
  responsibleName?: string
  responsibleRole?: string
  employeesCount?: string
  description?: string
  logoUrl?: string
  socialLinks?: Record<string, string>
  createdAt?: string
  updatedAt?: string
}

const SOCIAL_ICONS: Record<string, React.ElementType> = {
  linkedin: Linkedin,
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  youtube: Youtube,
}

function Field({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</p>
      <p className="mt-0.5 text-sm text-slate-800 break-words">{value}</p>
    </div>
  )
}

function CompanyDetailModal({ company, onClose }: { company: Company; onClose: () => void }) {
  const name = company.tradeName ?? company.commercialName ?? company.displayName ?? 'Empresa'
  const initial = name.charAt(0).toUpperCase()
  const socialEntries = Object.entries(company.socialLinks ?? {}).filter(([, v]) => v)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-6 py-4">
          <h2 className="text-lg font-bold text-slate-900">Perfil de empresa</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Identity */}
          <div className="flex items-center gap-4">
            {company.logoUrl ? (
              <img src={company.logoUrl} alt={name} className="h-16 w-16 rounded-xl object-contain border border-slate-100 bg-slate-50" />
            ) : (
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-2xl font-bold text-amber-400">
                {initial}
              </div>
            )}
            <div>
              <p className="text-xl font-bold text-slate-900">{name}</p>
              {company.legalName && <p className="text-sm text-slate-500">{company.legalName}</p>}
              {company.industry && (
                <span className="mt-1 inline-block rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                  {company.customIndustry && company.industry === 'Otro' ? company.customIndustry : company.industry}
                </span>
              )}
            </div>
          </div>

          {/* Description */}
          {company.description && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Descripción</p>
              <p className="mt-1 text-sm text-slate-700 leading-relaxed">{company.description}</p>
            </div>
          )}

          {/* Grid de datos */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="RFC" value={company.rfc} />
            <Field label="Empleados" value={company.employeesCount} />
            <Field label="Responsable" value={company.responsibleName} />
            <Field label="Cargo del responsable" value={company.responsibleRole} />
            <Field label="Teléfono" value={company.phone} />
            <Field label="WhatsApp" value={company.whatsapp} />
            <Field label="Correo" value={company.email} />
            <Field label="Dirección" value={company.address} />
            <Field label="Ciudad" value={company.city} />
            <Field label="Estado" value={company.state} />
            <Field label="País" value={company.country} />
            <Field label="Registro" value={company.createdAt?.slice(0, 10)} />
            <Field label="Última actualización" value={company.updatedAt?.slice(0, 10)} />
          </div>

          {/* Website */}
          {company.website && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Sitio web</p>
              <a
                href={company.website}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-amber-600 hover:underline"
              >
                <ExternalLink size={13} />
                {company.website}
              </a>
            </div>
          )}

          {/* Redes sociales */}
          {socialEntries.length > 0 && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400 mb-2">Redes sociales</p>
              <div className="flex flex-wrap gap-2">
                {socialEntries.map(([net, url]) => {
                  const Icon = SOCIAL_ICONS[net] ?? ExternalLink
                  return (
                    <a
                      key={net}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition capitalize"
                    >
                      <Icon size={13} />
                      {net}
                    </a>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function AdminEmpresas() {
  const [companies, setCompanies] = useState<Company[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<Company | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const fetchCompanies = async () => {
      setLoading(true)
      try {
        const filter = search
          ? { $or: [{ tradeName: { $regex: search, $options: 'i' } }, { commercialName: { $regex: search, $options: 'i' } }, { displayName: { $regex: search, $options: 'i' } }, { legalName: { $regex: search, $options: 'i' } }, { industry: { $regex: search, $options: 'i' } }] }
          : {}
        const res = await lumi.entities.companies.list({
          filter,
          sort: { createdAt: -1 },
          limit: PAGE_SIZE,
          skip: page * PAGE_SIZE,
        })
        setCompanies(res.list as Company[])
        setTotal(res.total ?? res.list.length)
      } catch (e) {
        console.error('[AdminEmpresas] error:', e)
      } finally {
        setLoading(false)
      }
    }
    fetchCompanies()
  }, [search, page, reloadKey])

  const handleDelete = async (c: Company) => {
    const name = c.tradeName ?? c.commercialName ?? c.displayName ?? 'esta empresa'
    if (!confirm(`¿Eliminar el perfil de "${name}"? Esta acción no se puede deshacer.`)) return
    setDeletingId(c._id)
    try {
      await lumi.entities.companies.delete(c._id)
      setReloadKey((k) => k + 1)
    } catch (e) {
      console.error('[AdminEmpresas] delete error:', e)
      alert('No se pudo eliminar la empresa. Inténtalo de nuevo.')
    } finally {
      setDeletingId(null)
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <AdminLayout title="Empresas" subtitle={`Supervisa todos los perfiles corporativos registrados en Beeznz Work · ${total} total`}>
      {selected && <CompanyDetailModal company={selected} onClose={() => setSelected(null)} />}

      <div className="mb-5 relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0) }}
          placeholder="Buscar por nombre, razón social o giro…"
          className="w-full max-w-md rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
        />
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(8)].map((_, i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-slate-100" />)}</div>
      ) : companies.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-16 text-center">
          <Building2 size={32} className="mb-3 text-slate-300" />
          <p className="font-semibold text-slate-600">Sin resultados</p>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Empresa</th>
                  <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 sm:table-cell">Giro</th>
                  <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 md:table-cell">Ciudad</th>
                  <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 lg:table-cell">Empleados</th>
                  <th className="hidden px-4 py-3 text-left text-xs font-semibold text-slate-500 lg:table-cell">Registro</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {companies.map((c) => (
                  <tr key={c._id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        {c.logoUrl ? (
                          <img src={c.logoUrl} alt="" className="h-8 w-8 rounded-lg object-contain border border-slate-100 bg-slate-50" />
                        ) : (
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-xs font-bold text-amber-400">
                            {(c.tradeName ?? c.commercialName ?? c.displayName ?? 'E').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-slate-900">{c.tradeName ?? c.commercialName ?? c.displayName ?? '—'}</p>
                          {c.email ? <p className="text-xs text-slate-400">{c.email}</p> : null}
                        </div>
                      </div>
                    </td>
                    <td className="hidden px-4 py-3 text-slate-500 sm:table-cell">{c.industry ?? '—'}</td>
                    <td className="hidden px-4 py-3 text-slate-500 md:table-cell">{[c.city, c.state].filter(Boolean).join(', ') || '—'}</td>
                    <td className="hidden px-4 py-3 text-slate-500 lg:table-cell">{c.employeesCount ?? '—'}</td>
                    <td className="hidden px-4 py-3 text-xs text-slate-400 lg:table-cell">{c.createdAt?.slice(0, 10) ?? '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {c.website ? (
                          <a href={c.website} target="_blank" rel="noreferrer" className="rounded p-1 text-slate-400 hover:text-amber-600"><Globe size={14} /></a>
                        ) : null}
                        {c.phone ? (
                          <a href={`tel:${c.phone}`} className="rounded p-1 text-slate-400 hover:text-amber-600"><Phone size={14} /></a>
                        ) : null}
                        <button
                          onClick={() => setSelected(c)}
                          className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 transition"
                        >
                          Ver perfil
                        </button>
                        <button
                          onClick={() => handleDelete(c)}
                          disabled={deletingId === c._id}
                          title="Eliminar empresa"
                          className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                        >
                          <Trash2 size={13} /> {deletingId === c._id ? 'Eliminando…' : 'Eliminar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <p>Página {page + 1} de {totalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium transition hover:bg-slate-50 disabled:opacity-40"><ChevronLeft size={14} /> Anterior</button>
                <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium transition hover:bg-slate-50 disabled:opacity-40">Siguiente <ChevronRight size={14} /></button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </AdminLayout>
  )
}

export default AdminEmpresas
